import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import { DownOutlined } from '@ant-design/icons';
import { Drawer, Button, Dropdown, Menu } from 'antd';
import classes from './Lanes.module.scss';
import DataTable from '../../components/DataTable/DataTable';
import StringFormatter from '../../shared/stringFormatter';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTools, faRoute } from "@fortawesome/free-solid-svg-icons";
import NewLane from '../../components/NewLane/NewLane';
import EditLane from "../../components/EditLane/EditLane";
import { withRouter } from 'react-router-dom';
import { isNotNullOrUndefined, isStringNotEmpty } from '../../shared/objectUtils';
import { CanDo } from '../../shared/entitlements/entitlements';
import { selectListIsLoading, selectListPagination, selectListRecords } from '../../store/utility';

const stringFormatter = new StringFormatter();

const Lanes = (props) => {
    //#region constants

    const fullWidth = global.window.innerWidth;
    const singularEntityName = "Lane";
    const pluralEntityName = "Lanes";

    //#endregion
    //#region useDispatch and useSelectors

    const dispatch = useDispatch();
    const records = useSelector(state => selectListRecords(state.lanes.lists, 'ALL'));
    const isLoading = useSelector(state => selectListIsLoading(state.lanes.lists, 'ALL'));
    const pagination = useSelector(state => selectListPagination(state.lanes.lists, 'ALL'));

    //#endregion
    //#region useStates

    const [selectedRecord, setSelectedRecord] = useState({});
    const [showNewEntity, setShowNewEntity] = useState(false);
    const [showEditEntity, setShowEditEntity] = useState(false);

    //#endregion
    //#region toggles

    const toggleNewEntity = () => {
        setShowNewEntity(!showNewEntity);
    };

    const toggleEditEntity = () => {
        setShowEditEntity(!showEditEntity);
    };

    //#endregion
    //#region goTos

    const goToLane = (id) => {
        props.history.push({ pathname: `/lanes/${id}` }, {
            previousPageTitle: "Lanes",
            previousPageLocation: props.location,
            previousBreadcrumbs: props.breadcrumbs,
        });
    };

    //#endregion
    //#region table methods

    const refreshTable = () => {
        let searchParams = {
            page: 1,
            size: 5,
            isDeleted: false
        };

        dispatch(actionCreators.fetchLaneList('ALL', searchParams));
    };

    const handleTableChange = (pagination, filters, sorter) => {
        const pager = { ...pagination };
        pager.current = pagination.current;
        dispatch(actionCreators.fetchLaneListSuccess('ALL', { params: { pagination: pager } }));

        let searchParams = {
            page: pagination.current,
            size: pagination.pageSize,
            isDeleted: false
        };

        if (isStringNotEmpty(sorter.order)) {
            const sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
            searchParams.order = sortOrder;
        }
        if (isStringNotEmpty(sorter.field)) {
            searchParams.sort = sorter.field;
        }

        dispatch(actionCreators.fetchLaneList('ALL', searchParams));
    };

    //#endregion
    //#region table displays

    const menu = (record) => {
        return (
            <Menu>
                {CanDo({ entityAction: 'UPDATE', entityModel: 'LANE', entityObject: record }) && (
                    <Menu.Item key="editEntity" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); toggleEditEntity(); }}>
                        <FontAwesomeIcon className="anticon" icon={faRoute} style={{ marginRight: 8 }} />
                        <span>Edit {singularEntityName} Details</span>
                    </Menu.Item>
                )}
                {CanDo({ entityAction: 'READ', entityModel: 'LANE', entityObject: record }) && (
                    <Menu.Item key="goToDashboard" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); goToLane(record.id); }}>
                        <span>Go to {singularEntityName} Dashboard</span>
                    </Menu.Item>
                )}
            </Menu>
        );
    };

    const columns = [
        {
            title: <FontAwesomeIcon icon={faTools} size="lg" />,
            key: 'actions',
            className: classes.columnNoBreak,
            width: 50,
            render: (text, record) => {
                return (
                    <Dropdown overlay={menu(record)} trigger={['click']}>
                        <Button>Actions <DownOutlined /></Button>
                    </Dropdown>
                );
            },
            align: 'center',
        },
        {
            title: 'Lane Id',
            dataIndex: 'irisId',
            key: 'irisId',
        },
        {
            title: 'Origin City',
            dataIndex: 'originCity',
            key: 'originCity',
        },
        {
            title: 'Origin State',
            dataIndex: 'originState',
            key: 'originState',
        },
        {
            title: 'Destination City',
            dataIndex: 'destinationCity',
            key: 'destinationCity',
        },
        {
            title: 'Destination State',
            dataIndex: 'destinationState',
            key: 'destinationState',
        },
        {
            title: 'Trailer Type',
            dataIndex: ['trailerType', 'description'],
            key: 'trailerType.description',
        },
        {
            title: 'Approx Dist',
            dataIndex: 'approxDistance',
            key: 'approxDistance',
            render: (text, record) => { return stringFormatter.toFormattedString("Distance", text, record.approxDistanceUnit); },
        }
    ];

    if (CanDo({ staffOnly: true })) {
        columns.push({
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            render: (text, record) => {
                if (isNotNullOrUndefined(text)) {
                    return (
                        <>
                            <span>{stringFormatter.toFormattedString("Money", text, null)}</span>
                            <br />
                            <span>Last Updated At:</span>
                            <br />
                            <span>{stringFormatter.toFormattedString("MomentDateTime", record.rateLastUpdatedAt, null)}</span>
                        </>
                    );
                } else {
                    return '';
                }
            },
        });
    }

    //#endregion
    //#region displays

    const newEntityComponents = (
        <Drawer
            title={"New " + singularEntityName}
            onClose={toggleNewEntity}
            visible={showNewEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <NewLane
                cancel={toggleNewEntity}
            />
        </Drawer>
    );

    const editEntityComponents = (
        <Drawer
            title={"Edit " + singularEntityName}
            onClose={toggleEditEntity}
            visible={showEditEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <EditLane
                cancel={toggleEditEntity}
                lane={selectedRecord}
            />
        </Drawer>
    );

    //#endregion
    //#region useEffects

    useEffect(() => {
        refreshTable();
    }, []);

    //#endregion

    return (
        <DataTable
            dataSource={records}
            columns={columns}
            pagination={pagination}
            onChange={handleTableChange}
            loading={isLoading === true}
            rowClassName={classes.dataTableRow}
            singularEntityName={singularEntityName}
            pluralEntityName={pluralEntityName}
            newEntityAction={CanDo({ entityAction: 'CREATE', entityModel: 'LANE' }) ? toggleNewEntity : null}
            rowKey={record => record.id}
            style={{ backgroundColor: '#ffffff' }}
        >
            {newEntityComponents}
            {editEntityComponents}
        </DataTable>
    );
};

export default withRouter(Lanes);